import {ClientAnalytics, useNavigate} from '@shopify/hydrogen';
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  disposeCollectedProducts,
  MemberCollectedProductPayloadType,
  saveMyCollectedProduct,
  saveMyProductWatchlist,
  SaveMyProductWatchlistPayloadType,
} from '../backendApi';
import EditMemberCollectedProductModal from '../components/modals/EditMemberCollectedProductModal.client';

import SwalComponent, {
  SwalLink,
} from '../components/modals/SwalComponent.client';
import {IndexPageViewType, ModalName, TwlAnalyticsEvent} from '../constants';
import {useFormState, useModalToggle} from '../hooks';
import {
  MemberCollectedProductStatusType,
  MemberCollectedProductType,
  MemberProductType,
} from '../types';
import {useAuthService} from './AuthServiceProvider.client';
import {
  RatingModalDataType,
  useRatingModal,
} from './RatingModalProvider.client';

interface MemberProductContextProps {
  myMemberProduct: MemberProductType;
  myMemberCollectedProduct: MemberCollectedProductType;
  setMyMemberProduct: (memberProduct: MemberProductType) => void;
  setMyMemberCollectedProduct: (
    memberCollectedProduct: MemberCollectedProductType,
  ) => void;
  toggleCollectProduct: (
    isAlreadyCollected: boolean,
    data: MemberCollectedProductPayloadType,
  ) => void;
  toggleWatchProduct: (data: SaveMyProductWatchlistPayloadType) => void;
  openRatingModal: (data: RatingModalDataType) => void;
}

const MemberProductContext = createContext<MemberProductContextProps>(null);

interface MemberProductProviderProps {
  children: ReactNode | Array<ReactNode>;
  memberProduct?: MemberProductType;
  memberCollectedProduct?: MemberCollectedProductType;
  refetch?: () => void;
}

function MemberProductProvider({
  children,
  memberProduct,
  memberCollectedProduct,
  refetch,
}: MemberProductProviderProps): ReactElement {
  const [myMemberProduct, setMyMemberProduct] =
    useState<MemberProductType>(null);

  const [myMemberCollectedProduct, setMyMemberCollectedProduct] =
    useState<MemberCollectedProductType>(null);

  const {authUser, checkLoginRequired} = useAuthService();

  useEffect(() => {
    if (memberProduct) setMyMemberProduct(authUser ? memberProduct : null);
  }, [authUser, memberProduct]);

  useEffect(() => {
    if (memberCollectedProduct)
      setMyMemberCollectedProduct(authUser ? memberCollectedProduct : null);
  }, [authUser, memberCollectedProduct]);

  const {
    openModal: openEditMemberCollectedProductModal,
    closeModal: closeEditMemberCollectedProductModal,
    isOpen: isOpenEditMemberCollectedProductModal,
    modalData: memberCollectedProductUuid, // MemberCollectedProductType.uuid
  } = useModalToggle<string>(ModalName.editMemberCollectedProduct);

  // --- Collect Product /
  const {
    handlePayloadSubmit: submitCollectProductPayload,
    responseData: collectProductResponseData,
  } = useFormState<
    MemberCollectedProductPayloadType,
    MemberCollectedProductType
  >(saveMyCollectedProduct, null, {
    onSuccess: (data) => {
      setMyMemberCollectedProduct(data);
      // Re-populate myMemberProduct to change the icon-collection highlight state
      refetch?.();
      ClientAnalytics.publish(
        data.status === MemberCollectedProductStatusType.disposed.value
          ? TwlAnalyticsEvent.REMOVE_FROM_COLLECTION
          : TwlAnalyticsEvent.ADD_TO_COLLECTION,
        true,
        {
          ...ClientAnalytics.getPageAnalyticsData(),
          productId: data.product?.uuid,
        },
      );
    },
  });

  const removeProductFromCollection = useCallback(
    async (productId: string) => {
      try {
        const {updated} = await disposeCollectedProducts(productId);
        // Re-populate myMemberProduct to change the icon-collection highlight state
        refetch?.();
        ClientAnalytics.publish(
          TwlAnalyticsEvent.REMOVE_FROM_COLLECTION,
          true,
          {
            ...ClientAnalytics.getPageAnalyticsData(),
            productId,
          },
        );
        SwalComponent.fire({
          title: 'Success!',
          icon: 'success',
          text: `${updated} collected product${
            updated === 1 ? '' : 's'
          } removed from your collection.`,
        }).then(() => SwalComponent.close());
      } catch (error) {
        SwalComponent.close();
      }
    },
    [refetch],
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (collectProductResponseData) {
      const {status, uuid} = collectProductResponseData;
      const isAddedToCollection =
        status !== MemberCollectedProductStatusType.disposed.value;
      const actionText = isAddedToCollection ? 'added to' : 'removed from';
      SwalComponent.fire({
        title: `Product ${actionText} your Collection`,
        icon: 'success',
        html: (
          <>
            {isAddedToCollection && (
              <>
                <p className="title-4 mb-50">
                  Would you like to add more details about your collected
                  product?
                </p>
                <button
                  type="button"
                  className="button mt-10"
                  onClick={() => {
                    SwalComponent.close();
                    openEditMemberCollectedProductModal(uuid);
                  }}
                >
                  Add Detail
                </button>
              </>
            )}
            <SwalLink
              navigate={navigate}
              to="/my/collection"
              className="button_type_ghost button ml-10 mt-10"
            >
              View My Collection
            </SwalLink>
            <button
              type="button"
              className="button button_color_gray_1 ml-10 mt-10"
              onClick={() => SwalComponent.close()}
            >
              Close
            </button>
          </>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMyMemberProduct, collectProductResponseData]);

  const toggleCollectProduct = useCallback(
    (isAlreadyCollected: boolean, data: MemberCollectedProductPayloadType) => {
      checkLoginRequired(() => {
        if (!isAlreadyCollected) {
          submitCollectProductPayload(data);
        } else {
          SwalComponent.fire({
            customClass: {
              title: 'title-3 swal2-title-no-margin',
            },
            title: 'This product is in your collection.',
            icon: 'warning',
            html: (
              <>
                <p className="title-4 mb-30">
                  If you remove this product from your collection, all the
                  collected units of this product will be removed from your
                  collection.
                </p>
                <SwalLink
                  navigate={navigate}
                  to={`/my/collection?view=${IndexPageViewType.list}&product_id=${data.product_id}`}
                  className="button_type_ghost button ml-10 mt-10"
                >
                  Update my collection
                </SwalLink>
                <button
                  type="button"
                  className="button ml-10 mt-10"
                  onClick={() => removeProductFromCollection(data.product_id)}
                >
                  Remove from collection
                </button>
                <button
                  type="button"
                  className="button button_color_gray_1 ml-10 mt-10"
                  onClick={() => SwalComponent.close()}
                >
                  Cancel
                </button>
              </>
            ),
          });
        }
      });
    },
    [
      checkLoginRequired,
      submitCollectProductPayload,
      navigate,
      removeProductFromCollection,
    ],
  );
  // --- / Collect Product

  // --- Watch Product /
  const {
    handlePayloadSubmit: submitWatchProductPayload,
    responseData: watchProductResponseData,
  } = useFormState<SaveMyProductWatchlistPayloadType, MemberProductType>(
    saveMyProductWatchlist,
    null,
    {
      onSuccess: (data) =>
        ClientAnalytics.publish(
          data.is_watched
            ? TwlAnalyticsEvent.ADD_TO_WATCHLIST
            : TwlAnalyticsEvent.REMOVE_FROM_WATCHLIST,
          true,
          {
            ...ClientAnalytics.getPageAnalyticsData(),
            productId: data.product?.uuid,
          },
        ),
    },
  );

  useEffect(() => {
    if (watchProductResponseData) {
      setMyMemberProduct(watchProductResponseData);
      const actionText = watchProductResponseData.is_watched
        ? 'added to'
        : 'removed from';
      SwalComponent.fire({
        title: `Product ${actionText} your Watch List!`,
        html: (
          <>
            <button
              type="button"
              className="button"
              onClick={() => SwalComponent.close()}
            >
              OK
            </button>
            <SwalLink
              navigate={navigate}
              to="/my/watchlist"
              className="button button_color_gray_1 ml-10"
            >
              My Watch List
            </SwalLink>
          </>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMyMemberProduct, watchProductResponseData]);

  const toggleWatchProduct = useCallback(
    (data: SaveMyProductWatchlistPayloadType) =>
      checkLoginRequired(() => submitWatchProductPayload(data)),
    [checkLoginRequired, submitWatchProductPayload],
  );
  // --- / Watch Product

  // --- Rate Product /
  const {openRatingModal} = useRatingModal();

  const checkPermissionOpenRatingModal = useCallback(
    (data: RatingModalDataType) => {
      const dataWithSuccessHandler = {...data};
      if (!dataWithSuccessHandler.onSuccess) {
        dataWithSuccessHandler.onSuccess = (updatedMemberProduct) =>
          setMyMemberProduct(updatedMemberProduct);
      }
      checkLoginRequired(() => openRatingModal(dataWithSuccessHandler));
    },
    [checkLoginRequired, openRatingModal],
  );
  // --- / Rate Product

  return (
    <MemberProductContext.Provider
      value={{
        myMemberProduct,
        myMemberCollectedProduct,
        setMyMemberProduct,
        setMyMemberCollectedProduct,
        toggleCollectProduct,
        toggleWatchProduct,
        openRatingModal: checkPermissionOpenRatingModal,
      }}
    >
      {children}
      <EditMemberCollectedProductModal
        uuid={memberCollectedProductUuid}
        isOpen={isOpenEditMemberCollectedProductModal}
        onRequestClose={closeEditMemberCollectedProductModal}
        isProductRequest={false}
        isNewRecord={true}
      />
    </MemberProductContext.Provider>
  );
}

function useMemberProduct(): MemberProductContextProps {
  const context = useContext(MemberProductContext);
  if (context === undefined) {
    throw new Error(
      'useMemberProduct must be used within MemberProductContext',
    );
  }

  return context;
}

export {MemberProductProvider, useMemberProduct};
